import { useState, useEffect } from 'react';
import { Settings, Bitcoin, Save, Loader2, Building2, Mail, Phone, CheckCircle, Server, Eye, EyeOff, Send, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [settings, setSettings] = useState({
    btcWalletAddress: '',
    btcEnabled: true,
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: 'EngineShipParts',
    emailEnabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      // Fall back to public settings
      try {
        const publicResponse = await axios.get(`${API_URL}/api/settings`);
        setSettings(prev => ({ ...prev, ...publicResponse.data }));
      } catch (e) {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const openTestEmailDialog = () => {
    if (!settings.smtpHost || !settings.smtpUser || !settings.smtpPassword) {
      toast.error('Please fill in and save all SMTP fields first');
      return;
    }
    setTestEmailAddress(settings.companyEmail || '');
    setTestEmailDialogOpen(true);
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    
    setTestingEmail(true);
    try {
      // First save the settings
      await axios.put(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then send test email
      const response = await axios.post(`${API_URL}/api/settings/test-smtp`, 
        { testEmail: testEmailAddress },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message || 'Test email sent successfully!');
      setTestEmailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Site Settings</h1>
          <p className="text-slate-500 mt-1">Configure payment, email, and company information</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Bitcoin Payment Settings */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Bitcoin className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Bitcoin Payment Settings</h2>
              <p className="text-sm text-slate-500">Configure your BTC wallet for receiving payments</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Bitcoin Payments</Label>
                <p className="text-sm text-slate-500">Allow customers to pay with Bitcoin</p>
              </div>
              <Switch
                checked={settings.btcEnabled}
                onCheckedChange={(checked) => handleChange('btcEnabled', checked)}
                data-testid="btc-enabled-switch"
              />
            </div>

            <div>
              <Label htmlFor="btcWallet" className="text-sm font-medium">
                Bitcoin Wallet Address
              </Label>
              <Input
                id="btcWallet"
                value={settings.btcWalletAddress}
                onChange={(e) => handleChange('btcWalletAddress', e.target.value)}
                placeholder="bc1q..."
                className="mt-1.5 font-mono text-sm"
                data-testid="btc-wallet-input"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                This is where customer payments will be sent. Make sure it's correct!
              </p>
            </div>

            {settings.btcWalletAddress && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">
                  Wallet configured: <span className="font-mono">{settings.btcWalletAddress.slice(0, 20)}...</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Email / SMTP Settings */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Server className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Email Settings (SMTP)</h2>
              <p className="text-sm text-slate-500">Configure email for order confirmations</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Order Confirmation Emails</Label>
                <p className="text-sm text-slate-500">Send confirmation emails to buyers after checkout</p>
              </div>
              <Switch
                checked={settings.emailEnabled}
                onCheckedChange={(checked) => handleChange('emailEnabled', checked)}
                data-testid="email-enabled-switch"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtpHost" className="text-sm font-medium">SMTP Host</Label>
                <Input
                  id="smtpHost"
                  value={settings.smtpHost || ''}
                  onChange={(e) => handleChange('smtpHost', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="mt-1.5"
                  data-testid="smtp-host-input"
                />
              </div>
              <div>
                <Label htmlFor="smtpPort" className="text-sm font-medium">SMTP Port</Label>
                <Input
                  id="smtpPort"
                  type="number"
                  value={settings.smtpPort || 587}
                  onChange={(e) => handleChange('smtpPort', parseInt(e.target.value))}
                  placeholder="587"
                  className="mt-1.5"
                  data-testid="smtp-port-input"
                />
              </div>
              <div>
                <Label htmlFor="smtpUser" className="text-sm font-medium">SMTP Username</Label>
                <Input
                  id="smtpUser"
                  value={settings.smtpUser || ''}
                  onChange={(e) => handleChange('smtpUser', e.target.value)}
                  placeholder="your-email@gmail.com"
                  className="mt-1.5"
                  data-testid="smtp-user-input"
                />
              </div>
              <div>
                <Label htmlFor="smtpPassword" className="text-sm font-medium">SMTP Password / App Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="smtpPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={settings.smtpPassword || ''}
                    onChange={(e) => handleChange('smtpPassword', e.target.value)}
                    placeholder="••••••••••••"
                    className="pr-10"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="smtpFromEmail" className="text-sm font-medium">From Email</Label>
                <Input
                  id="smtpFromEmail"
                  type="email"
                  value={settings.smtpFromEmail || ''}
                  onChange={(e) => handleChange('smtpFromEmail', e.target.value)}
                  placeholder="noreply@engineshipparts.com"
                  className="mt-1.5"
                  data-testid="smtp-from-email-input"
                />
              </div>
              <div>
                <Label htmlFor="smtpFromName" className="text-sm font-medium">From Name</Label>
                <Input
                  id="smtpFromName"
                  value={settings.smtpFromName || ''}
                  onChange={(e) => handleChange('smtpFromName', e.target.value)}
                  placeholder="EngineShipParts"
                  className="mt-1.5"
                  data-testid="smtp-from-name-input"
                />
              </div>
            </div>

            {settings.emailEnabled && settings.smtpHost && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">
                  Email configured via {settings.smtpHost}
                </span>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Gmail Setup Instructions</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Go to Google Account → Security → 2-Step Verification (enable it)</li>
                <li>Go to App passwords → Create new app password</li>
                <li>Use the 16-character password as SMTP Password above</li>
                <li>SMTP Host: smtp.gmail.com, Port: 587</li>
              </ol>
            </div>

            {/* Test SMTP Button */}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={openTestEmailDialog}
                disabled={!settings.smtpHost || !settings.smtpUser || !settings.smtpPassword}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="test-smtp-btn"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Send Test Email
              </Button>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Company Information</h2>
              <p className="text-sm text-slate-500">Basic company details shown across the site</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="companyName" className="text-sm font-medium">
                Company Name
              </Label>
              <Input
                id="companyName"
                value={settings.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="EngineShipParts"
                className="mt-1.5"
                data-testid="company-name-input"
              />
            </div>

            <div>
              <Label htmlFor="companyEmail" className="text-sm font-medium flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Company Email
              </Label>
              <Input
                id="companyEmail"
                type="email"
                value={settings.companyEmail}
                onChange={(e) => handleChange('companyEmail', e.target.value)}
                placeholder="sales@engineshippart.com"
                className="mt-1.5"
                data-testid="company-email-input"
              />
            </div>

            <div>
              <Label htmlFor="companyPhone" className="text-sm font-medium flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Company Phone
              </Label>
              <Input
                id="companyPhone"
                value={settings.companyPhone}
                onChange={(e) => handleChange('companyPhone', e.target.value)}
                placeholder="+1 (201) 234-4851"
                className="mt-1.5"
                data-testid="company-phone-input"
              />
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Settings className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800">Important Notes</h3>
              <ul className="text-sm text-amber-700 mt-1 space-y-1 list-disc list-inside">
                <li>Changes to BTC wallet address take effect immediately for new orders</li>
                <li>Order confirmation emails are sent automatically when email is enabled</li>
                <li>Always verify your wallet address and SMTP settings before saving</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to verify your SMTP configuration is working correctly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="testEmail">Send test email to:</Label>
              <Input
                id="testEmail"
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                className="mt-1.5"
                data-testid="test-email-address-input"
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p><strong>SMTP Server:</strong> {settings.smtpHost}:{settings.smtpPort}</p>
              <p><strong>From:</strong> {settings.smtpFromName} &lt;{settings.smtpFromEmail || settings.companyEmail}&gt;</p>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={sendTestEmail} 
                disabled={testingEmail || !testEmailAddress}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="send-test-email-btn"
              >
                {testingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
